import Vue from "vue"
import { store } from "@/store/stores"
import api from "@/api"
import userService from "./userService"
import ownHealthFeatureAccess from "./ownHealthFeatureAccess"
import healthConnect from "./healthConnectService"
import appleHealth from "./appleHealthImportService"
import { healthDay, moveHealthDay } from "./healthStatistics"
import { healthSearchDay, healthWorkoutWindow } from "./healthImportMatching"
import { matchHealthActivities } from "./healthActivityMatching"
import { healthWorkoutEnergy } from "./healthPresentation"

// Volatile, account-scoped view of device data. Never commit these values to Vuex,
// localStorage or an API: only provider + record ID can leave the device when linking.
class HealthActivityService {
  constructor() {
    this.state = Vue.observable({ owner: "", scope: "", workouts: [], executions: [], links: {}, recordings: {}, cacheRevision: 0,
      from: "", to: "", busy: false, error: "", linkError: "", truncated: false, complete: false, automaticLinking: false, linking: "" })
    this.generation = 0
    this.reads = new Map()
    this.recordingRevision = 0
    this.loadVersion = 0
  }

  scope() {
    const health = healthConnect.state, owner = String(userService.getUserId() || "")
    return ownHealthFeatureAccess.isAllowed() && health.android && health.enabled && owner && health.ownerId === owner && health.grantedCategories.includes("workouts")
      ? `${owner}:${health.permissionKey}:${ownHealthFeatureAccess.state.revision}` : ""
  }
  current(scope, generation) { return Boolean(scope && scope === this.scope() && scope === this.state.scope && generation === this.generation) }
  reset() {
    this.generation++; this.loadVersion++; this.recordingRevision++; this.state.cacheRevision++; this.reads.clear()
    Object.assign(this.state, { owner: "", scope: "", workouts: [], executions: [], links: {}, recordings: {}, from: "", to: "",
      busy: false, error: "", linkError: "", truncated: false, complete: false, automaticLinking: false, linking: "" })
  }
  ensureScope() {
    const scope = this.scope()
    if (scope !== this.state.scope) {
      this.reset()
      this.state.scope = scope
      this.state.owner = scope ? String(userService.getUserId() || "") : ""
    }
    return scope
  }
  start() {
    if (this.unwatch) return
    this.unwatch = store.watch(() => `${this.scope()}|${healthConnect.state.lastSyncAt}|${healthConnect.state.busy}`, () => {
      if (!this.ensureScope() || healthConnect.state.busy) return
      this.load(this.state.to || healthDay(), true)
    })
    this.unsubscribeAccess = ownHealthFeatureAccess.subscribe(() => { this.ensureScope() })
    if (this.ensureScope() && !healthConnect.state.busy) this.load()
  }
  stop() { this.unwatch?.(); this.unwatch = null; this.unsubscribeAccess?.(); this.unsubscribeAccess = null; this.reset() }

  async load(day = healthDay(), force = false) {
    const scope = this.ensureScope()
    if (!scope) return
    const from = moveHealthDay(day, -6), to = day
    if (!force && this.state.from === from && this.state.to === to && !this.state.error) return
    const generation = this.generation, version = ++this.loadVersion
    const current = () => this.current(scope, generation) && version === this.loadVersion
    this.state.busy = true; this.state.error = ""; this.state.linkError = ""
    if (force) { this.recordingRevision++; this.state.cacheRevision++; this.state.recordings = {}; this.reads.clear() }
    try {
      // Refresh the native grant before reading, including permission changes made outside Roody.
      if (!await appleHealth.available() || !current()) return
      const range = { from: healthSearchDay(from).from, to: healthSearchDay(to).to }
      const results = await Promise.allSettled([
        appleHealth.search(range, { allowOffline: true }), api.workoutApi.getHealthWorkoutCandidates(range),
      ])
      if (!current()) return
      const [external, own] = results
      if (external.status !== "fulfilled") throw external.reason
      if (own.status !== "fulfilled") await ownHealthFeatureAccess.reject(ownHealthFeatureAccess.capture(), own.reason)
      if (!current()) return
      const cached = (store.getters["runWorkoutStore/hubGroupedSessions"] || []).map(item => item.execution)
      const executions = (own.status === "fulfilled" ? own.value.executions : cached) || []
      this.state.executions = executions.filter(item => item && String(item.user_id?._id || item.user_id || "") === this.state.owner)
      const links = { ...this.state.links }
      for (const execution of this.state.executions) links[execution._id] = execution.healthImport || null
      this.state.links = links
      this.state.workouts = external.value.workouts.map(workout => {
        const execution = this.state.executions.find(item => item.healthImport?.workoutId === workout.workoutId)
        return workout.existingLink || !execution ? workout : { ...workout, existingLink: { executionId: String(execution._id), title: execution.title || "Training" } }
      })
      this.state.from = from; this.state.to = to
      this.state.truncated = external.value.truncated || own.value?.truncated === true
      this.state.complete = own.status === "fulfilled" && !this.state.truncated && external.value.linkStatusAvailable
      this.state.automaticLinking = external.value.automaticLinking && this.state.complete
      if (!external.value.linkStatusAvailable || own.status !== "fulfilled") this.state.linkError = "Zuordnungen sind gerade nicht erreichbar. Aktualisiere mit Internetverbindung, um Trainings zu verbinden."
      const matches = this.matches().filter(match => match.automatic)
      for (const match of matches) {
        if (!current() || !this.state.automaticLinking) break
        try { await this.link(match.executionId, match.workout, true) }
        catch (_) { break } // One actionable failure; never retry a failing write in a loop.
      }
      // The timeline gets measured calories without persisting them in its workout.
      const linked = this.state.workouts.filter(item => item.existingLink).slice(0, 30)
      for (let index = 0; index < linked.length && current(); index += 3) {
        await Promise.allSettled(linked.slice(index, index + 3).map(item => this.read(item.workoutId)))
      }
    } catch (error) {
      if (current()) { this.state.error = error?.response?.data?.message || error.message || "Aktivitäten konnten nicht geladen werden."; this.state.complete = false; this.state.automaticLinking = false }
    } finally { if (current()) this.state.busy = false }
  }

  matches() {
    if (!this.ensureScope()) return []
    return matchHealthActivities(this.state.workouts, this.state.executions.map(item => ({ ...item, healthImport: this.linkFor(item) })), this.state.owner, this.state.automaticLinking)
  }
  linkFor(execution) {
    if (!this.scope() || this.state.scope !== this.scope() || String(execution?.user_id?._id || execution?.user_id || "") !== this.state.owner) return null
    return Object.prototype.hasOwnProperty.call(this.state.links, execution._id) ? this.state.links[execution._id] : execution.healthImport || null
  }
  recordingFor(execution) { const link = this.linkFor(execution); return link?.provider === "health_connect" ? this.state.recordings[link.workoutId] || null : null }
  energy(execution) { return healthWorkoutEnergy(execution, this.recordingFor(execution)) }

  async read(workoutId) {
    const scope = this.ensureScope(), generation = this.generation, revision = this.recordingRevision
    if (!scope) return null
    if (this.state.recordings[workoutId]) return this.state.recordings[workoutId]
    if (this.reads.has(workoutId)) return this.reads.get(workoutId)
    const request = appleHealth.read(workoutId).then(workout => {
      if (!this.current(scope, generation) || revision !== this.recordingRevision || workout?.provider !== "health_connect" || workout.workoutId !== workoutId) return null
      const keys = Object.keys(this.state.recordings)
      if (keys.length >= 200) Vue.delete(this.state.recordings, keys[0])
      Vue.set(this.state.recordings, workoutId, workout)
      return workout
    }).finally(() => { if (this.reads.get(workoutId) === request) this.reads.delete(workoutId) })
    this.reads.set(workoutId, request)
    return request
  }

  noteLink(executionId, reference, workout, automatic = false) {
    if (!this.ensureScope()) return
    const previous = this.state.links[executionId]
    Vue.set(this.state.links, executionId, reference)
    const target = this.state.executions.find(item => String(item._id) === String(executionId))
    this.state.workouts = this.state.workouts.map(item => {
      const wasLinked = String(item.existingLink?.executionId || "") === String(executionId)
      if (reference?.workoutId === item.workoutId) return { ...item, existingLink: { executionId: String(executionId), title: target?.title || "Training", automatic } }
      if (wasLinked) return { ...item, existingLink: null, dismissedExecutionIds: [...new Set([...(item.dismissedExecutionIds || []), String(executionId)])] }
      return item
    })
    if (previous && !reference) Vue.delete(this.state.recordings, previous.workoutId)
    if (workout?.provider === "health_connect" && workout.workoutId === reference?.workoutId) Vue.set(this.state.recordings, workout.workoutId, workout)
  }

  async link(executionId, workout, automatic = false) {
    const scope = this.ensureScope(), generation = this.generation, access = ownHealthFeatureAccess.capture()
    if (!scope || this.state.linking || !this.state.complete || (automatic && !this.state.automaticLinking)) throw new Error("Bitte die Aktivitäten erneut aktualisieren.")
    const target = this.state.executions.find(item => String(item._id) === String(executionId))
    if (!target || target.healthImportRestricted || this.linkFor(target) || workout.existingLink) throw new Error("Die Zuordnung hat sich geändert. Bitte erneut aktualisieren.")
    this.state.linking = workout.workoutId; this.state.linkError = ""
    try {
      // This operation deliberately sends no timestamps, pulse or calories.
      const result = await api.workoutApi.linkHealthWorkout(executionId, { provider: "health_connect", workoutId: workout.workoutId }, automatic ? "automatic" : "manual", automatic ? target.updatedAt : null)
      if (this.current(scope, generation)) this.noteLink(executionId, result.healthImport, null, automatic)
      return result
    } catch (error) {
      await ownHealthFeatureAccess.reject(access, error)
      if (this.current(scope, generation)) { this.state.linkError = error?.response?.data?.message || "Die Zuordnung konnte nicht gespeichert werden. Bitte erneut aktualisieren."; this.state.complete = false; this.state.automaticLinking = false }
      throw error
    } finally { if (this.current(scope, generation)) this.state.linking = "" }
  }

  async unlink(workout) {
    const scope = this.ensureScope(), generation = this.generation, access = ownHealthFeatureAccess.capture()
    if (!scope || this.state.linking || !workout.existingLink) return
    const executionId = workout.existingLink.executionId
    this.state.linking = workout.workoutId; this.state.linkError = ""
    try {
      await api.workoutApi.unlinkHealthWorkout(executionId, workout.workoutId)
      if (this.current(scope, generation)) this.noteLink(executionId, null)
    } catch (error) {
      await ownHealthFeatureAccess.reject(access, error)
      if (this.current(scope, generation)) this.state.linkError = error?.response?.data?.message || "Die Zuordnung konnte nicht gelöst werden. Bitte erneut versuchen."
      throw error
    } finally { if (this.current(scope, generation)) this.state.linking = "" }
  }

  entries(day = "") {
    if (!this.scope() || this.state.scope !== this.scope()) return []
    const workouts = this.state.workouts
    const linkedIds = new Set(workouts.map(item => String(item.existingLink?.executionId || "")).filter(Boolean))
    const result = workouts.map(workout => {
      const execution = this.state.executions.find(item => String(item._id) === String(workout.existingLink?.executionId || "")) || null
      return { id: workout.workoutId, workout, execution, title: execution?.title || workout.existingLink?.title || workout.activityName || "Training",
        from: Date.parse(workout.startedAt), to: Date.parse(workout.endedAt), exact: true,
        duration: workout.durationSeconds, energy: healthWorkoutEnergy(execution, this.state.recordings[workout.workoutId]),
      }
    })
    for (const execution of this.state.executions) {
      if (linkedIds.has(String(execution._id))) continue
      const window = healthWorkoutWindow(execution)
      result.push({ id: String(execution._id), execution, workout: null, title: execution.title || "Training", ...window,
        duration: Number(execution.duration), energy: this.energy(execution) })
    }
    return result.filter(item => {
      const date = Number.isFinite(item.to) ? healthDay(new Date(item.to - 1)) : ""
      return day ? date === day : date >= this.state.from && date <= this.state.to
    }).sort((a, b) => b.from - a.from)
  }
}

export default new HealthActivityService()
